<script setup lang="ts">
import ContestsRewardModal from './ContestsRewardModal.vue'

import { getRewardDefaultNotificationText } from '../utils'

import type { ContestGameReward, LoyaltyReward, MessageTemplate, RewardTypeSlug } from '@/types/types'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'
import { fetchMerchantRewardById } from '@/components/rewards/utils/rewardApi'

const props = defineProps<{
  reward: ContestGameReward
  messageTemplate: MessageTemplate
  channelType: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:reward', reward: ContestGameReward): void
}>()

const notification = useNotification()
const { t } = useI18n()
const { fetchAllRewards } = useReward()
const { rewardVouchers, isRewardTypesReady, rewardTypesError } = useRewardForm()
const { user } = useLocalStorage()
const { noSpecialCharacters } = useRegex()
const { isPolite } = useMerchantTexts()

const editingReward = ref<ContestGameReward>({
  ...props.reward,
  value: props.reward?.value ? props.reward.value / 100 : null,
  rewardId: props.reward?.rewardId
})

const selectedVoucher = computed({
  get: () => editingReward.value.rewardTypeId ?? null,
  set: (id: number) => {
    if (!id) return
    editingReward.value.rewardTypeId = id
    editingReward.value.rewardTypeSlug = rewardVouchers.value.find((v) => v.id === id)?.slug
  }
})

const rewards = ref<LoyaltyReward[]>(null)

const isRewardLibraryOpen = ref<boolean>(false)
const librarySelectedReward = ref<LoyaltyReward | null>(null)
const isCreateEditRewardModalOpen = ref<boolean>(false)

const invalidCharacters = computed(
  () => props.channelType === 'sms' && noSpecialCharacters(editingReward.value?.notificationTextOverride)
)

const rewardTypeSlug = computed((): RewardTypeSlug => {
  const foundVoucher = rewardVouchers.value.find((voucher) => voucher.id === editingReward.value?.rewardTypeId)
  return foundVoucher ? foundVoucher?.slug : editingReward.value?.rewardTypeSlug
})

const saveReward = () => {
  if (invalidCharacters.value) {
    notification.create('error', {
      text: t('ui.error.strings.unauthorizedEmoji'),
      timeout: 2000
    })
    return
  }

  emit('update:reward', {
    ...editingReward.value,
    rewardTypeSlug: rewardTypeSlug.value,
    value: editingReward.value?.value ? editingReward.value.value * 100 : null
  })
  emit('close')
}

const closeWithLoadError = (text = t('pages.reward.errors.fetchReward')) => {
  notification.create('error', { text })
  isRewardLibraryOpen.value = false
  isCreateEditRewardModalOpen.value = false
  emit('close')
}

const applySelectedLibraryReward = (
  libraryReward: LoyaltyReward | null | undefined,
  contestReward?: ContestGameReward
): boolean => {
  if (!libraryReward?.id || !libraryReward.rewardType?.slug) {
    closeWithLoadError()
    return false
  }
  if (!isRewardTypesReady.value) {
    closeWithLoadError(rewardTypesError.value || t('pages.reward.errors.fetchReward'))
    return false
  }
  const voucherId = rewardVouchers.value.find((v) => v.slug === libraryReward.rewardType.slug)?.id
  if (!voucherId) {
    closeWithLoadError()
    return false
  }
  selectedVoucher.value = voucherId

  let points = contestReward?.points ?? props.reward.points
  if (contestReward?.points === 0) {
    points = null
  }

  let validityInDays = contestReward?.validityInDays ?? props.reward.validityInDays
  if (contestReward?.validityInDays === 0) {
    validityInDays = null
  }

  let notificationTextOverride = ''
  if (contestReward?.notificationTextOverride !== undefined && contestReward?.notificationTextOverride !== null) {
    notificationTextOverride = contestReward.notificationTextOverride
  } else {
    notificationTextOverride = props.reward.notificationTextOverride
  }

  if (!notificationTextOverride) {
    notificationTextOverride = getRewardDefaultNotificationText(t, isPolite.value)
  }

  editingReward.value.probability = contestReward?.probability ?? props.reward.probability
  editingReward.value.points = points
  editingReward.value.validityInDays = validityInDays
  editingReward.value.notificationTextOverride = notificationTextOverride

  editingReward.value.name = libraryReward.name
  editingReward.value.value = libraryReward.value ? libraryReward.value / 100 : editingReward.value.value
  editingReward.value.minimumOrderAmount = (libraryReward as any).minimumOrderAmount
    ? Number((libraryReward as any).minimumOrderAmount)
    : editingReward.value.minimumOrderAmount
  editingReward.value.rewardTypeSlug = libraryReward.rewardType.slug as RewardTypeSlug
  editingReward.value.externalId = libraryReward.externalId ?? editingReward.value.externalId
  editingReward.value.rewardId = libraryReward.id
  editingReward.value.pictureUrl = libraryReward.pictureUrl

  librarySelectedReward.value = libraryReward
  return true
}

const handleSelectLibraryReward = async ({
  rewardId,
  openCreateEditRewardModal
}: {
  rewardId: number
  openCreateEditRewardModal?: boolean
}) => {
  isRewardLibraryOpen.value = false

  await handleCreateOrUpdateReward({ rewardId, openCreateEditRewardModal })
}

const handleCreateReward = () => {
  librarySelectedReward.value = null

  isRewardLibraryOpen.value = false
  isCreateEditRewardModalOpen.value = true
}

const fetchRewards = async () => {
  const data = await fetchAllRewards()
  if (!Array.isArray(data)) throw new Error('Unable to load reward library')
  rewards.value = data
}

const handleCreateOrUpdateReward = async ({
  rewardId,
  openCreateEditRewardModal,
  contestReward
}: {
  rewardId: number
  openCreateEditRewardModal?: boolean
  contestReward?: ContestGameReward
}) => {
  try {
    const reward = await fetchMerchantRewardById(user.value.merchant.id, rewardId)
    if (!applySelectedLibraryReward(reward, contestReward)) return

    isCreateEditRewardModalOpen.value = !!openCreateEditRewardModal

    if (!openCreateEditRewardModal) saveReward()
  } catch {
    closeWithLoadError()
  }
}

const openLibrary = async () => {
  await fetchRewards()
  isRewardLibraryOpen.value = true
}

const openExistingReward = async (rewardId: number) => {
  const reward = await fetchMerchantRewardById(user.value.merchant.id, rewardId)
  if (reward == null) {
    notification.create('error', { text: t('pages.contests.rewards.missingLibraryReward') })
    await openLibrary()
    return
  }
  if (!applySelectedLibraryReward(reward)) return

  isCreateEditRewardModalOpen.value = true
}

onMounted(async () => {
  try {
    if (props.reward?.rewardId) {
      await openExistingReward(props.reward.rewardId)
    } else {
      await openLibrary()
    }
  } catch {
    closeWithLoadError()
  }
})
</script>

<template>
  <RewardSelectionModal
    v-if="rewards"
    :is-open="isRewardLibraryOpen"
    :rewards="rewards"
    show-edit-button
    @close-modal="emit('close')"
    @select-reward="(rewardId) => handleSelectLibraryReward({ rewardId, openCreateEditRewardModal: false })"
    @create-reward="handleCreateReward"
  />

  <ContestsRewardModal
    v-if="isCreateEditRewardModalOpen"
    v-model:contest-reward="editingReward"
    :selected-reward="librarySelectedReward"
    :channel-type="channelType"
    dissociate-on-cancel
    @close="emit('close')"
    @selected-reward:saved="
      handleCreateOrUpdateReward({ rewardId: $event.rewardId, contestReward: $event.contestReward })
    "
  />
</template>
